import { defineStore } from 'pinia';
import { ref, computed, type Ref } from 'vue';
import {useApiClient} from "@/hooks/api-hook.ts";
import type {Charter, CharterVipUpdateRequest} from "@/api/client/models/index.ts";

export const useCharterFlightsStore = defineStore('charter-flights', () => {
  const api = useApiClient();

  // State
  const charterFlights: Ref<Charter[]> = ref([]);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  // Getters
  const charterFlightsCount = computed(() => charterFlights.value.length);
  const hasCharterFlights = computed(() => charterFlights.value.length > 0);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => error.value !== null);

  // Actions
  async function fetchCharterFlights(isArchived: boolean = false): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const result = await api.charter.get({queryParameters: {isArchived}})
      charterFlights.value = result ?? [];
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch charter flights';
      console.error('Error fetching charter flights:', err);
    } finally {
      loading.value = false;
    }
  }

  async function archiveCharterFlight(id: number): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await api.charter.byCharterId(id).archive.post();
      charterFlights.value = charterFlights.value.filter((flight) => flight.id !== id);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to archive charter flight';
      console.error('Error archiving charter flight:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateCharterVip(request: CharterVipUpdateRequest): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await api.charter.vip.put(request);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update charter flight VIP status';
      console.error('Error updating charter flight VIP status:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function clearCharterFlights() {
    charterFlights.value = [];
    error.value = null;
  }

  function clearError() {
    error.value = null;
  }

  return {
    // State
    charterFlights,
    loading,
    error,

    // Getters
    charterFlightsCount,
    hasCharterFlights,
    isLoading,
    hasError,

    // Actions
    fetchCharterFlights,
    archiveCharterFlight,
    updateCharterVip,
    clearCharterFlights,
    clearError,

    // Backward compatibility
    refresh: fetchCharterFlights
  };
});
